import React from 'react';
import { Card, CardContent, Typography, Theme, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppSelector } from '../../app/hooks';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import { IUnresolvedItemAnchor } from '../../redux/features/unresolvedItems/unresolvedItems.props';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';

export enum UnresolvedItemVariant {
  NOT_ON_MENU = 'notOnMenu',
  INVALID_OPTION = 'invalidOption',
  QUANTITY_ESCALATION = 'quantityEscalation',
  NO_DEFAULT_OPTION = 'noDefaultOption',
  UNMATCHED_COUPON = 'unmatchedCoupon',
}

interface Props {
  variant: UnresolvedItemVariant;
  title: string;
  diagnosticLabel: string;
  // HITL-1621: the customer utterance that triggered this card. Rendered as a
  // quoted line with its clock time; absent (or empty utterance) renders nothing.
  anchor?: IUnresolvedItemAnchor;
  // HITL-1621: scrolls the transcript to the anchored line and pulses it.
  // Rendered only when the anchor has a target line.
  onShowInTranscript?: () => void;
  // HITL-1621: whole-card click (quantity cards scroll to the capped cart item).
  onCardClick?: () => void;
}

const BACKGROUND_COLOR: Record<UnresolvedItemVariant, string> = {
  [UnresolvedItemVariant.NOT_ON_MENU]: Colors.apricot,
  [UnresolvedItemVariant.INVALID_OPTION]: Colors.beeswax,
  [UnresolvedItemVariant.QUANTITY_ESCALATION]: Colors.spindle,
  [UnresolvedItemVariant.NO_DEFAULT_OPTION]: Colors.teaGreen,
  [UnresolvedItemVariant.UNMATCHED_COUPON]: Colors.mauve,
};

// Clock label for the anchored utterance ("3:45 PM"), locale-forced like the
// transcript's own timestamps. '' for a missing/invalid timestamp.
const formatAnchorTime = (timestamp?: string): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const useStyles = makeStyles<
  Theme,
  { variant: UnresolvedItemVariant; isHitl3: boolean; clickable: boolean }
>(({ spacing }: Theme) => ({
  container: ({ variant, isHitl3, clickable }) => ({
    // HITL 3.0 reduces the inter-card gap to the shared 8px rhythm.
    marginTop: isHitl3 ? HITL3_PADDING : spacing(2),
    backgroundColor: `${BACKGROUND_COLOR[variant]} !important`,
    cursor: clickable ? 'pointer' : 'default',
  }),
  // CardContent = the mainItemname block's parent. HITL 3.0 gives it an even
  // 8px padding on all sides; legacy keeps MUI's default with the tight bottom.
  content: ({ isHitl3 }) =>
    isHitl3
      ? { padding: `${HITL3_PADDING} !important` }
      : { paddingBottom: `${spacing(0.5)} !important` },
  mainItemname: {
    '& h6': {
      lineHeight: '1.57 !important',
      fontSize: `${spacing(1.5)} !important`,
      fontWeight: 'bold !important',
    },
    // HITL 3.0 removes the label block's bottom padding.
    paddingBottom: ({ isHitl3 }) => (isHitl3 ? 0 : spacing(1)),
  },
  diagnosticLabel: {
    fontStyle: 'italic',
  },
  // HITL-1621: quoted triggering utterance — a muted inset block so it reads as
  // context, not as another diagnostic line.
  anchorQuote: {
    borderLeft: `${spacing(0.375)} solid ${Colors.silverChalice}`,
    paddingLeft: spacing(1),
    marginTop: spacing(0.5),
    fontSize: '12px !important',
    color: `${Colors.daintree} !important`,
    wordBreak: 'break-word',
  },
  anchorTime: {
    fontVariantNumeric: 'tabular-nums',
    color: Colors.silverChalice,
    marginRight: spacing(0.75),
    fontSize: '10px',
  },
  showInTranscript: {
    display: 'inline-block !important',
    marginTop: `${spacing(0.5)} !important`,
    fontSize: '12px !important',
    fontWeight: 'bold !important',
    cursor: 'pointer',
  },
}));

/**
 * Unified diagnostic card for unresolved items — covers "not on menu",
 * "invalid option", "quantity escalation", "no default option", and
 * "unmatched coupon" variants. Dismissal is handled by the parent panel's
 * "Dismiss & Repeat" button. HITL-1621 adds the triggering-utterance quote
 * ("Show in transcript") and an optional whole-card click.
 */
export default function UnresolvedItemCard({
  variant,
  title,
  diagnosticLabel,
  anchor,
  onShowInTranscript,
  onCardClick,
}: Props) {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const classes = useStyles({ variant, isHitl3, clickable: !!onCardClick });

  const anchorTime = formatAnchorTime(anchor?.timestamp);
  const hasAnchorQuote = !!anchor?.utterance;
  const canShowInTranscript = !!anchor?.messageId && !!onShowInTranscript;

  const handleShowInTranscript = (e: React.MouseEvent) => {
    // Keep the link from also triggering the whole-card click.
    e.stopPropagation();
    onShowInTranscript?.();
  };

  return (
    <Card elevation={3} className={classes.container} onClick={onCardClick}>
      <CardContent className={classes.content}>
        <div className={classes.mainItemname}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="subtitle2" className={classes.diagnosticLabel}>
            ({diagnosticLabel})
          </Typography>
        </div>
        {hasAnchorQuote && (
          <Typography variant="body2" className={classes.anchorQuote}>
            {anchorTime && (
              <span className={classes.anchorTime}>{anchorTime}</span>
            )}
            &ldquo;{anchor?.utterance}&rdquo;
          </Typography>
        )}
        {canShowInTranscript && (
          <Link
            component="button"
            type="button"
            underline="hover"
            className={classes.showInTranscript}
            onClick={handleShowInTranscript}
          >
            Show in transcript ↗
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
